import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import {
  ContributionDetailBody,
  type ContributionDetail,
} from '@/components/giving/contribution-detail-body';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatMoney } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

interface FundSummary {
  id: string;
  name: string;
}

export const ContributionDetailPage = () => {
  const { contributionId = '' } = useParams();
  const { t } = useTranslation('giving');
  const locale = usePreferences((state) => state.locale);

  const detail = useQuery({
    queryKey: ['giving', 'contributions', contributionId],
    queryFn: () => api.get<ContributionDetail>(`/giving/contributions/${contributionId}`),
    enabled: Boolean(contributionId),
  });

  const fund = useQuery({
    queryKey: ['giving', 'funds', detail.data?.fundId],
    queryFn: () => api.get<FundSummary>(`/giving/funds/${detail.data!.fundId}`),
    enabled: Boolean(detail.data?.fundId),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <GivingSubnav />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const row = detail.data;
  if (!row) {
    return (
      <div className="space-y-4">
        <GivingSubnav />
        <PageHeader
          title={t('contributions.detail.notFound')}
          description={t('contributions.detail.notFoundBody')}
          breadcrumbs={[{ label: t('contributions.title'), to: '/giving' }]}
        />
      </div>
    );
  }

  const fundName = fund.data?.name;
  const giftDate = formatDate(row.contributionDate, { locale, style: 'medium' });

  return (
    <div className="space-y-4">
      <GivingSubnav />
      <PageHeader
        title={t('contributions.receiptDated', {
          number: row.receiptNumber,
          date: giftDate,
        })}
        description={
          fundName
            ? t('contributions.detail.description', {
                amount: formatMoney(row.amountMinor, row.currency, { locale }),
                fund: fundName,
                date: giftDate,
              })
            : formatMoney(row.amountMinor, row.currency, { locale })
        }
        breadcrumbs={[
          { label: t('contributions.title'), to: '/giving' },
          ...(fundName ? [{ label: fundName, to: `/giving/funds/${row.fundId}` }] : []),
        ]}
        headerActions={
          row.isVoided ? <Badge variant="danger">{t('contributions.voided')}</Badge> : undefined
        }
      />

      <Card>
        <CardContent className="pt-4">
          <ContributionDetailBody contributionId={contributionId} showIntro={false} />
        </CardContent>
      </Card>
    </div>
  );
};
